import type { ComponentType } from 'react';
import {
  faCircleQuestion,
  faComments,
  faCreditCardFront,
  faEnvelope,
  faFileLines,
  faGear,
  faHouse,
  faImage,
  faPenToSquare,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Link, usePage } from '@inertiajs/react';

import { ReceivedReviewsIndicator } from '@/components/reviews-hub/ui/received-reviews-indicator';
import LawLyticsLogo from '@/components/shared/lawlytics-logo';
import ModalButton from '@/components/shared/modal-button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Icon } from '@/components/ui/icon';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import lawLyticsLogo from '@/images/lawlytics-logo.svg';
import {
  cmsAssetsPath,
  cmsBillingPath,
  cmsDashboardPath,
  cmsFormsPath,
  cmsPagesEditorIndexPath,
  cmsPromptLogsPath,
  cmsReviewsOverviewPath,
  cmsSettingsPagePath,
  cmsSupportPath,
} from '@/lib/routes';
import type { Permissions, SharedData, UserRole } from '@/types/shared';
import NavUser from './nav-user';
import { SwitchAccountDropdown } from './switch-account-dropdown';

type AppSettingItemId = 'settings' | 'billing' | 'support';

interface BaseNavItem {
  title: string;
  icon: typeof faHouse;
  prefetch?: boolean;
  Indicator?: ComponentType;
  requiresPermission?: keyof Permissions;
  url: string;
}

interface LinkNavItem extends BaseNavItem {
  kind: 'link';
}

interface ModalNavItem extends BaseNavItem {
  kind: 'modal';
}

type NavItem = LinkNavItem | ModalNavItem;

type AppSettingItem = NavItem & { id: AppSettingItemId };

function isActiveNav(currentUrl: string, itemUrl: string): boolean {
  return currentUrl === itemUrl || currentUrl.startsWith(`${itemUrl}/`);
}

function canAccessBilling(userRole?: UserRole): boolean {
  return userRole === 'root' || userRole === 'billing';
}

function resolveVisibleAppSettingIds(
  accountActive: boolean | undefined,
  userRole?: UserRole,
): AppSettingItemId[] {
  const ids: AppSettingItemId[] = ['support'];

  if (accountActive && userRole !== 'billing') {
    ids.push('settings');
  }

  if (canAccessBilling(userRole)) {
    ids.push('billing');
  }

  return ids;
}

function getVisibleMainItems(items: typeof mainNavItems, permissions: Permissions) {
  return items.filter((item) => !item.requiresPermission || permissions[item.requiresPermission]);
}

function DefaultSidebar() {
  return (
    <>
      <SidebarHeader className="mt-4">
        <div className="px-4 group-data-[collapsible=icon]:hidden">
          <LawLyticsLogo />
        </div>
        <Avatar className="hidden h-8 w-8 rounded-lg group-data-[collapsible=icon]:block">
          <AvatarImage src={lawLyticsLogo} alt="LawLytics Logo" />
          <AvatarFallback className="rounded-lg">LL</AvatarFallback>
        </Avatar>
      </SidebarHeader>
      <SidebarContent>
        <MainNavigation />
      </SidebarContent>
      <SidebarFooter>
        <SidebarSeparator className="mx-0" />
        <AppSettings />
        <SidebarSeparator className="mx-0" />
        <NavUser />
      </SidebarFooter>
    </>
  );
}

const mainNavItems: LinkNavItem[] = [
  {
    kind: 'link',
    title: 'Dashboard',
    url: cmsDashboardPath(),
    icon: faHouse,
  },
  {
    kind: 'link',
    title: 'Pages',
    url: cmsPagesEditorIndexPath(),
    icon: faPenToSquare,
  },
  {
    kind: 'link',
    title: 'Assets',
    url: cmsAssetsPath(),
    icon: faImage,
  },
  {
    kind: 'link',
    title: 'Reviews',
    url: cmsReviewsOverviewPath(),
    icon: faComments,
    Indicator: ReceivedReviewsIndicator,
  },
  {
    kind: 'link',
    title: 'Leads',
    url: cmsFormsPath(),
    icon: faEnvelope,
  },
  {
    kind: 'link',
    title: 'Prompt Logs',
    url: cmsPromptLogsPath(),
    icon: faFileLines,
    requiresPermission: 'promptLogs',
  },
];

function MainNavigation() {
  const { url } = usePage();
  const { accountActive, currentUser, permissions = {} } = usePage<SharedData>().props;
  const userRole = currentUser?.role;
  const isBillingUser = userRole === 'billing';

  if (!accountActive) {
    return null;
  }

  const visibleItems = getVisibleMainItems(mainNavItems, permissions);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {visibleItems.map((item) => {
            const isActive = isActiveNav(url, item.url);
            const isDisabled = isBillingUser;

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={isActive}>
                  <Link
                    href={item.url}
                    aria-current={isActive ? 'page' : undefined}
                    aria-disabled={isDisabled}
                    prefetch={item.prefetch}
                  >
                    <Icon icon={item.icon} />
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                      <span className="truncate">{item.title}</span>
                      {item.Indicator ? <item.Indicator /> : null}
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

const appSettingsItems: AppSettingItem[] = [
  {
    id: 'settings',
    kind: 'link',
    title: 'Settings',
    url: cmsSettingsPagePath({ tab: 'site-general' }),
    icon: faGear,
  },
  {
    id: 'billing',
    kind: 'link',
    title: 'Billing',
    url: cmsBillingPath(),
    icon: faCreditCardFront,
  },
  {
    id: 'support',
    kind: 'modal',
    title: 'Support',
    url: cmsSupportPath(),
    icon: faCircleQuestion,
  },
];

function AppSettings() {
  const { url } = usePage();
  const { accountActive, currentUser } = usePage<SharedData>().props;
  const visibleAppSettingIds = resolveVisibleAppSettingIds(accountActive, currentUser?.role);
  const visibleItems = appSettingsItems.filter((item) => visibleAppSettingIds.includes(item.id));

  return (
    <SidebarMenu>
      <SwitchAccountDropdown />

      {visibleItems.map((item) => {
        const isActive = isActiveNav(url, item.url);

        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton asChild isActive={isActive}>
              {item.kind === 'modal' ? (
                <ModalButton href={item.url}>
                  <Icon icon={item.icon} />
                  <span>{item.title}</span>
                </ModalButton>
              ) : (
                <Link
                  href={item.url}
                  aria-current={isActive ? 'page' : undefined}
                  prefetch={item.prefetch}
                >
                  <Icon icon={item.icon} />
                  <span>{item.title}</span>
                </Link>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

export default DefaultSidebar;
